import {
  Avatar,
  Box,
  createStyles,
  Group,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { motion } from 'framer-motion';
import React from 'react';

import { ReactComponent as Export } from '@portals/icons/linear/import-3.svg';
import {
  OrganizationConfigType,
  PartnerConfigurationType,
} from '@portals/types';
import { useDownloadFile } from '@portals/utils';

import { MenuItemType } from './SuccessCenter';

interface SuccessCenterItemsProps {
  successCenterMenuItems: Array<MenuItemType>;
  showSuccessCenterItems: boolean;
  partnerManuals:
    | OrganizationConfigType['partner_manuals']
    | undefined
    | PartnerConfigurationType['manuals'];
}

export function SuccessCenterItems({
  showSuccessCenterItems,
  successCenterMenuItems,
  partnerManuals,
}: SuccessCenterItemsProps) {
  const { classes } = useStyles();

  const downloadFile = useDownloadFile();

  const onDownloadFile = (fileUrl: string, fileName: string) => {
    downloadFile.mutate({ fileUrl, fileName });
  };

  if (!showSuccessCenterItems) {
    return (
      <motion.div
        initial={{ x: -300, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 300, opacity: 0 }}
        layout
        transition={{ duration: 0.3 }}
      >
        <Stack mt="xs">
          {partnerManuals?.map(
            ({
              file_name,
              manual_url,
              id,
              partner_display_name,
              partner_logo_url,
            }) => {
              return (
                <Group
                  key={id}
                  noWrap
                  position="apart"
                  className={classes.manufacturerManual}
                  p="md"
                  onClick={() => onDownloadFile(manual_url, file_name)}
                >
                  <Group>
                    <Avatar
                      src={partner_logo_url}
                      radius="xl"
                      className={classes.partnerLogo}
                    />

                    <Box>
                      <Text fz="xs" fw={500}>
                        {partner_display_name}
                      </Text>

                      <Text c="gray.7" fz="xs" truncate maw={200}>
                        {file_name}
                      </Text>
                    </Box>
                  </Group>

                  <Export width={18} height={18} />
                </Group>
              );
            }
          )}
        </Stack>
      </motion.div>
    );
  }

  return (
    <SimpleGrid cols={3} spacing="xs">
      {successCenterMenuItems.map(({ icon, label, onClick }) => {
        return (
          <Stack
            key={label}
            spacing="xs"
            align="center"
            className={classes.menuItem}
            onClick={onClick}
            data-testid={`success-center-${label}`}
            px="md"
            pt="xxl"
            pb="md"
            w={120}
          >
            {icon}

            <Text size="xs" color="gray.8" ta="center" h={33} w={90}>
              {label}
            </Text>
          </Stack>
        );
      })}
    </SimpleGrid>
  );
}

const useStyles = createStyles((theme) => ({
  menuItem: {
    borderRadius: theme.radius.lg,
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },

  manufacturerManual: {
    cursor: 'pointer',
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
  },

  partnerLogo: {
    border: `1px solid ${theme.colors.gray[2]}`,
  },
}));
